import { createAsyncThunk } from "@reduxjs/toolkit";
import { ROUTES } from "../../services/routes";
import api from "../../services/api";
import { mergeGuestCart, fetchCart } from "../cart/cartThunk";
import { clearGuestCookie } from "../../utils/guestId";

const getCurrentDomain = () => {
  if (typeof window === "undefined") return "";
  return window.location.host;
};

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (userData, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post(ROUTES.auth.login, userData);

      const { token, user } = res.data?.data || {};

      if (token && user) {
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));

        try {
          await dispatch(mergeGuestCart(user._id)).unwrap();
        } catch (e) {
          console.warn("Cart merge failed (non-critical):", e);
        }
        clearGuestCookie();
        localStorage.removeItem("cart_id");
        await dispatch(fetchCart());
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  },
);

export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async (formData, { rejectWithValue, dispatch }) => {
    try {
      const isFormData = formData instanceof FormData;
      const response = await api.post(ROUTES.auth.register, formData, {
        headers: {
          "Content-Type": isFormData
            ? "multipart/form-data"
            : "application/json",
        },
      });

      const { token, user } = response.data?.data || {};

      if (token && user) {
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));

        try {
          await dispatch(mergeGuestCart(user._id)).unwrap();
        } catch (e) {
          console.warn("Cart merge failed (non-critical):", e);
        }
        clearGuestCookie();
        localStorage.removeItem("cart_id");
        await dispatch(fetchCart());
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data ||
          "Registration failed",
      );
    }
  },
);

export const fetchOwnProfile = createAsyncThunk(
  "auth/fetchOwnProfile",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(ROUTES.user.updateOneProfile);
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch profile",
      );
    }
  },
);

export const updateOwnProfile = createAsyncThunk(
  "auth/updateOwnProfile",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.put(ROUTES.user.updateOneProfile, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Profile update failed",
      );
    }
  },
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async ({ email }, { rejectWithValue }) => {
    try {
      const domain = getCurrentDomain();
      const res = await api.post(ROUTES.auth.forgotPassword, { email, domain });
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to send OTP. Please try again.",
      );
    }
  },
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ email, otp, newPassword }, { rejectWithValue }) => {
    try {
      const domain = getCurrentDomain();
      const res = await api.post(ROUTES.auth.resetPassword, {
        email,
        otp,
        newPassword,
        domain,
      });
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Password reset failed. Please try again.",
      );
    }
  },
);

export const googleLoginUser = createAsyncThunk(
  "auth/googleLoginUser",
  async ({ credential, domain }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post(
        ROUTES.auth.googleLogin || "/auth/google-login",
        {
          credential,
          domain,
        },
      );

      const { token, user } = res.data?.data || {};

      if (token && user) {
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));
        try {
          await dispatch(mergeGuestCart(user._id)).unwrap();
        } catch (e) {
          console.warn("Cart merge failed:", e);
        }
        clearGuestCookie();
        localStorage.removeItem("cart_id");
        await dispatch(fetchCart());
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Google login failed",
      );
    }
  },
);
